package com.daytimegaming.ourdailyfinances.data.repository

import com.daytimegaming.ourdailyfinances.data.network.dto.AppleAppStoreSyncRequest
import com.daytimegaming.ourdailyfinances.data.network.dto.GooglePlaySyncRequest
import com.daytimegaming.ourdailyfinances.data.network.service.UserService
import com.daytimegaming.ourdailyfinances.domain.model.UserProfile
import com.daytimegaming.ourdailyfinances.domain.model.UserSubscription
import com.daytimegaming.ourdailyfinances.domain.model.toDomain
import com.daytimegaming.ourdailyfinances.domain.repository.AuthRepository
import com.daytimegaming.ourdailyfinances.domain.repository.UserRepository
import com.daytimegaming.ourdailyfinances.getPlatform
import kotlinx.coroutines.DelicateCoroutinesApi
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach

@OptIn(DelicateCoroutinesApi::class)
class UserRepositoryImpl(
    private val userService: UserService,
    authRepository: AuthRepository,
) : UserRepository {
    private val _currentUserProfile = MutableStateFlow<UserProfile?>(null)
    override val currentUserProfile: StateFlow<UserProfile?> = _currentUserProfile.asStateFlow()

    private var localTier: String? = null
    private var localActive: Boolean = false
    private var localSource: String? = null

    init {
        authRepository.currentUser()
            .onEach { firebaseUser ->
                if (firebaseUser == null) {
                    _currentUserProfile.value = null
                }
            }
            .launchIn(GlobalScope)
    }

    override fun saveLocalSubscription(tier: String, active: Boolean) {
        val platform = getPlatform().name

        localTier = tier
        localActive = active
        localSource = if (platform.startsWith("iOS", ignoreCase = true) || platform.startsWith("iPhone", ignoreCase = true)) {
            "apple"
        } else if (platform.startsWith("Android", ignoreCase = true)) {
            "google"
        } else {
            "web"
        }
        
        _currentUserProfile.value = _currentUserProfile.value?.let { profile ->
            profile.copy(
                subscription = UserSubscription(
                    active = active,
                    tier = tier,
                    source = localSource,
                    billingHistory = profile.subscription?.billingHistory ?: emptyList()
                )
            )
        }
    }

    override suspend fun syncGooglePlayPurchase(
        subscriptionId: String,
        purchaseToken: String,
        tier: String
    ) {
        saveLocalSubscription(tier = tier, active = true)
        try {
            val response = userService.syncGooglePlayPurchase(
                GooglePlaySyncRequest(
                    subscriptionId = subscriptionId,
                    purchaseToken = purchaseToken,
                    tier = tier
                )
            )
            _currentUserProfile.value = response.toDomain()
        } catch (e: Exception) {
            // Gracefully retains local subscription state if backend sync endpoint fails/unreachable
        }
    }

    override suspend fun syncAppleAppStorePurchase(
        transactionId: String,
        jwsRepresentation: String,
        tier: String
    ) {
        saveLocalSubscription(tier = tier, active = true)
        try {
            val response = userService.syncAppleAppStorePurchase(
                AppleAppStoreSyncRequest(
                    transactionId = transactionId,
                    jwsRepresentation = jwsRepresentation,
                    tier = tier
                )
            )
            _currentUserProfile.value = response.toDomain()
        } catch (e: Exception) {
            // Gracefully retains local subscription state if backend sync endpoint fails/unreachable
        }
    }

    override suspend fun getProfile(): UserProfile {
        val profile = userService.getUserMe().toDomain()
        val currentLocalTier = localTier
        val merged = if (localActive && currentLocalTier != null) {
            profile.copy(
                subscription = UserSubscription(
                    active = true,
                    tier = currentLocalTier,
                    source = localSource ?: profile.subscription?.source,
                    billingHistory = profile.subscription?.billingHistory ?: emptyList()
                )
            )
        } else {
            profile
        }
        _currentUserProfile.value = merged
        return merged
    }

    override suspend fun registerProfile(): UserProfile {
        val profile = userService.createUserMe().toDomain()
        _currentUserProfile.value = profile
        return profile
    }

    override suspend fun updateProfile(firstName: String?, lastName: String?): UserProfile {
        val profile = userService.updateUserMe(firstName, lastName).toDomain()
        _currentUserProfile.value = profile
        return profile
    }

    override suspend fun cancelSubscription(): UserProfile {
        localActive = false
        return try {
            val response = userService.cancelSubscription()
            val profile = response.toDomain()
            _currentUserProfile.value = profile
            profile
        } catch (e: Exception) {
            _currentUserProfile.value = _currentUserProfile.value?.let { profile ->
                profile.copy(
                    subscription = profile.subscription?.copy(active = false)
                )
            }
            _currentUserProfile.value ?: throw e
        }
    }

    override suspend fun registerDeviceToken(token: String) {
        try {
            userService.registerDeviceToken(token)
        } catch (e: Exception) {
            // Log/ignore network errors
        }
    }

    override suspend fun unregisterDeviceToken(token: String) {
        try {
            userService.unregisterDeviceToken(token)
        } catch (e: Exception) {
            // Log/ignore network errors
        }
    }

    override fun clearProfile() {
        _currentUserProfile.value = null
    }
}

